<script setup lang="ts">
/**
 * CandidateTile Component
 *
 * Reusable tile showing a single candidate's avatar, name, price, and buy button.
 * Used by ElectionCard in single, head-to-head, and scrollable layouts.
 */
import { useI18n } from 'vue-i18n';
import CandidateAvatar from '@/components/CandidateAvatar.vue';
import type { PartyCode } from '../types/election.types';
import { formatDollarPrice } from '@/utils/formatPrice';
import { useUserState } from '@/stores/useUserState';

const userState = useUserState();
const { t } = useI18n();

const props = withDefaults(
  defineProps<{
    conid: number;
    candidateName: string;
    party: PartyCode;
    displayPrice: number;
    /** Controls lazy-loading of avatar image (default: true) */
    showAvatar?: boolean;
    /** When true, the Yes button shows close state instead of buy */
    closeState?: boolean;
  }>(),
  {
    showAvatar: true,
    closeState: false,
  },
);

const emit = defineEmits<{
  buy: [conid: number, event: Event];
  close: [conid: number, event: Event];
  select: [conid: number];
}>();

const partyRingClass: Record<string, string> = {
  DEM: 'ring-democrat-subtle',
  GOP: 'ring-republican-subtle',
  IND: 'ring-independent-subtle',
  GRN: 'ring-green-subtle',
};

function partyTextClass(party: PartyCode): string {
  if (party === 'DEM') return 'text-democrat';
  if (party === 'GOP') return 'text-republican';
  if (party === 'GRN') return 'text-green';
  return 'text-independent';
}

function partyButtonClass(party: PartyCode): string {
  if (party === 'DEM')
    return 'border-democrat-subtle bg-democrat-subtle text-on-democrat-subtle hover:bg-democrat-subtle-hover';
  if (party === 'GOP')
    return 'border-republican-subtle bg-republican-subtle text-on-republican-subtle hover:bg-republican-subtle-hover';
  if (party === 'GRN') return 'border-green-subtle bg-green-subtle text-on-green-subtle hover:bg-green-subtle-hover';
  return 'border-independent-subtle bg-independent-subtle text-on-independent-subtle hover:bg-independent-subtle-hover';
}

function handleBuy(e: Event) {
  e.stopPropagation();
  if (props.closeState) {
    emit('close', props.conid, e);
  } else {
    emit('buy', props.conid, e);
  }
}
</script>

<template>
  <div :data-conid="conid" class="flex flex-col items-center gap-3">
    <div class="relative">
      <div
        class="h-14 w-14 overflow-hidden rounded-xl ring-[3px] ring-offset-2"
        :class="partyRingClass[party] || 'ring-independent-subtle'"
      >
        <CandidateAvatar
          v-if="showAvatar"
          :conid="conid"
          :candidate-name="candidateName"
          :party-code="party"
          size="fill"
        />
      </div>
    </div>
    <div class="text-center">
      <div
        class="w-24 cursor-pointer truncate text-xs font-black leading-normal hover:underline"
        :class="partyTextClass(party)"
        @click.stop="emit('select', conid)"
      >
        {{ candidateName }}
      </div>
      <div class="text-lg font-bold">
        {{ displayPrice > 0 ? `${displayPrice}%` : '--' }}
      </div>
      <button
        class="mt-1 cursor-pointer rounded-full border px-4 py-1.5 text-[10px] font-black uppercase tracking-tighter active:scale-[0.97] transition-all"
        :class="closeState ? 'border-accent bg-accent text-on-accent hover:opacity-90' : partyButtonClass(party)"
        @click="handleBuy"
      >
        <i v-if="closeState" class="ibi-close-x-filled mr-1 align-[-.1em]" />
        <template v-else>{{ t('yes') }} </template>
        ${{ formatDollarPrice(displayPrice / 100)
        }}<i v-if="!userState.authenticated" class="ibi-lock-closed ml-1 align-[-.1em]" />
      </button>
    </div>
  </div>
</template>
